import React, { useEffect, useState } from "react";
import { Box, LinearProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useLocation } from "react-router";
import { useUserAuth } from "../context/UserAuthContext";
import Header from "./Header";
import FullScreenMap from "./FullScreenMap";
import FarmsDropdowns from "./FarmsDropdowns";
import SearchBar from "./SearchBar";
import FarmsInfoPanel from "./FarmsInfoPanel"; // Import the FarmsInfoPanel (formerly RightDrawer)
import { useFarmSelection } from '../context/FarmSelectionContext';
import CustomLoadingScreen from './CustomLoadingScreen';
import { API_BASE_URL } from '../config';

const Home = () => {
  const { logOut, user } = useUserAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [idToken, setIdToken] = useState("");
  const location = useLocation();
  const { userData: userDataFromState } = location.state || {};
  const [userData, setUserData] = useState(userDataFromState || null);
  const [farmLocations, setFarmLocations] = useState([]);
  const [progress, setProgress] = useState(0);  // Track progress percentage
  const [popupInfo, setPopupInfo] = useState(null);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");  // State for search input
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedFarmTypes, setSelectedFarmTypes] = useState([]);
  const [isDataFetched, setIsDataFetched] = useState(false);  // Flag to check if data is ready
  const { setFarmsList } = useFarmSelection();

  // Fetch ID Token when the user logs in
  useEffect(() => {
    const fetchIdToken = async () => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setIdToken(token);
        } catch (error) {
          console.log("Error fetching ID token:", error);
        }
      }
    };

    fetchIdToken();
  }, [user]); // Only run this effect once when the user logs in

  // Fetch user profile if userData is missing but user is present
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user && user.phoneNumber && !userData && idToken) {
        try {
          const response = await fetch(`${API_BASE_URL}/user/${encodeURIComponent(user.phoneNumber)}/verify`, {
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const profile = await response.json();
            setUserData(profile);
          }
        } catch (error) {
          console.log('Error fetching user profile:', error);
        }
      }
    };
    fetchUserProfile();
  }, [user, userData, idToken]);

  // Fetch farm data based on the userData farms and their IDs
  useEffect(() => {
    const fetchFarmData = async () => {
      if (idToken && userData && !isDataFetched) {
        // Combine owned farms and invited farms
        const ownedFarms = userData.farms || [];
        const invitedFarms = userData.invited_farms || [];
        
        // Create a combined list of farm IDs, avoiding duplicates
        const allFarmIds = new Set();
        
        // Add owned farms
        ownedFarms.forEach(farm => {
          if (farm.id) allFarmIds.add(farm.id);
        });
        
        // Add invited farms
        invitedFarms.forEach(invitedFarm => {
          if (invitedFarm.farm_id) allFarmIds.add(invitedFarm.farm_id);
        });
        
        const farmIdsArray = Array.from(allFarmIds);
        
        if (farmIdsArray.length === 0) {
          setIsDataFetched(true);
          return;
        }
        
        const totalFarms = farmIdsArray.length;
        const promises = farmIdsArray.map(async (farmId, index) => {
          try {
            const response = await fetch(`${API_BASE_URL}/farm/${farmId}`, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
            });

            if (!response.ok) {
              if (response.status === 404) {
                return null; // Ignore 404 errors
              } else {
                throw new Error(`Error fetching farm ${farmId}: ${response.statusText}`);
              }
            }

            const farmData = await response.json();
            const lat = parseCoordinate(farmData.lat);
            const lon = parseCoordinate(farmData.lon);

            // Update the progress percentage after each farm is fetched
            setProgress(prevProgress => prevProgress + (100 / totalFarms));

            return {
              id: farmId,
              lat,
              lon,
              area: farmData.area,
              area_unit: farmData.area_unit,
              farm_size: farmData.farm_size,
              farm_size_unit: farmData.farm_size_unit,
              type: farmData.farm_type,
              name: farmData.name,
              city: farmData.city,
              state: farmData.state,
              country: farmData.country,
              zones: farmData.zones,
              inputs: farmData.inputs,
              invited_users: farmData.invited_users
            };

          } catch (error) {
            return null;
          }
        });

        const farmResults = await Promise.all(promises);
        const validFarms = farmResults.filter((farm) => farm !== null);
        setFarmLocations(validFarms);
        setFarmsList(validFarms);

        // When all data is fetched, set the flag to true
        setIsDataFetched(true);

        // FullScreenMap will handle viewState based on centroid and optimal zoom
      }
    };

    // Always try to fetch farm data if we have an idToken and userData
    if (idToken && userData) {
      fetchFarmData();
    }
  }, [idToken, userData, isDataFetched, setFarmsList]); // Added isDataFetched and setFarmsList to dependencies

  // Function to parse the latitude and longitude values
  const parseCoordinate = (coordinateStr) => {
    if (!coordinateStr) return NaN;
    const cleanStr = coordinateStr.replace(/[^\d.-]/g, '');
    const direction = coordinateStr.slice(-1);
    let coordinate = parseFloat(cleanStr);
    if (direction === 'S' || direction === 'W') {
      coordinate = -coordinate;
    }
    return coordinate;
  };

  // Handle user logout
  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/");
    } catch (error) {
      console.log(error.message);
    }
  };

  // Mobile-specific viewport handling
  useEffect(() => {
    const handleResize = () => {
      // Force viewport update on mobile
      if (isMobile) {
        const viewport = document.querySelector('meta[name=viewport]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isMobile]);

  // Robust loading check: if userData or farmLocations are missing, show loading
  if (!userData || (!userData.farms && !userData.invited_farms) || farmLocations.length === 0) {
    return <CustomLoadingScreen />;
  }

  return (
    <Box sx={{ height: "100vh", display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        zIndex: 1000, 
        flexShrink: 0, 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100%',
        height: '64px'
      }}>
        <Header
          onLogout={handleLogout}
          userData={userData}
          menuExpanded={menuExpanded}
          setMenuExpanded={setMenuExpanded}
          idToken={idToken}
          isMobile={isMobile}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </Box>

      {/* Main content area */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        height: '100vh',
        mt: '64px' // Add top margin for header
      }}>
        {/* Map container */}
        <Box sx={{ 
          flex: 1,
          position: 'relative',
          height: { xs: '55vh', md: '100%' },
          overflow: 'hidden'
        }}>
          {/* Show Progress Bar until data is fully fetched */}
          {!isDataFetched ? (
            <Box sx={{ 
              width: '100%', 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#181F2A'
            }}>
              <Typography variant="h6" align="center" sx={{ color: '#fff', mb: 2 }}>
                Fetching farm data...
              </Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ width: '80%', maxWidth: 400 }} />
            </Box>
          ) : (
            <FullScreenMap
              farmLocations={farmLocations}
              selectedCountries={selectedCountries}
              selectedStates={selectedStates}
              selectedCities={selectedCities}
              selectedFarmTypes={selectedFarmTypes}
              searchQuery={searchQuery}
              popupInfo={popupInfo}
              setPopupInfo={setPopupInfo}
              apiKey={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
            />
          )}
        </Box>

        {/* Desktop - Farms Info Panel on the right */}
        <Box sx={{ 
          width: { xs: '100%', md: '400px' }, 
          height: { xs: '45vh', md: '100%' },
          overflow: 'hidden',
          backgroundColor: '#181F2A',
          borderLeft: { xs: 'none', md: '1px solid rgba(255,255,255,0.1)' },
          borderTop: { xs: '1px solid rgba(255,255,255,0.1)', md: 'none' },
          display: { xs: 'block', md: 'block' }
        }}>
          <FarmsInfoPanel
            farmLocations={farmLocations}
            selectedCountries={selectedCountries}
            selectedStates={selectedStates}
            selectedCities={selectedCities}
            selectedFarmTypes={selectedFarmTypes}
            searchQuery={searchQuery}
            userData={userData}
            idToken={idToken}
            isMobile={isMobile}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Home;